/* eslint-disable react-refresh/only-export-components */
// Authentication context for NotesCrafter Main Site
// Aligned with the Print service authentication logic
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE_URL, apiGet, apiPost } from '../services/api';
import { registerFCMToken, unregisterFCMToken } from '../services/fcm';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
            use_fedcm_for_prompt?: boolean;
          }) => void;
          prompt: () => void;
          cancel: () => void;
          disableAutoSelect?: () => void;
        };
      };
    };
  }
}

const ONE_TAP_LOGOUT_KEY = 'nc_one_tap_logout_at';
const ONE_TAP_COOLDOWN_MS = 5 * 60 * 1000;

// Auth user type (from our Workers backend, not Firebase)
export interface AuthUser {
  id: number;
  email: string;
  name: string;
  profile_picture?: string;
  phone?: string;
  phone_verified?: boolean;
  created_at?: string;
}

export interface SubscriptionInfo {
  plan_type: 'trial' | 'monthly' | 'half_yearly' | 'annual' | 'lifetime';
  status: 'active' | 'expired' | 'cancelled';
  end_date: string;
  remaining_days?: number;
}

interface SessionResponse {
  success: boolean;
  data: {
    user: AuthUser;
    subscription: SubscriptionInfo | null;
    welcome_sale_eligible?: boolean;
  };
}

interface SubscriptionStatusResponse {
  success: boolean;
  data: {
    has_active_subscription: boolean;
    subscription: SubscriptionInfo | null;
    trial_available: boolean;
  };
}

interface AuthConfigResponse {
  success: boolean;
  data: {
    google_client_id: string;
  };
}

interface AuthContextType {
  // State
  user: AuthUser | null;
  subscription: SubscriptionInfo | null;
  trialAvailable: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasActiveSubscription: boolean;
  authError: string | null;
  welcomeSaleEligible: boolean;

  // Actions
  signIn: () => void;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<AuthUser | null>;
  refreshSubscription: () => Promise<void>;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Convert error codes to user-friendly messages
 */
function getHumanReadableError(errorCode: string | null): string {
  const errorMessages: Record<string, string> = {
    'access_denied': 'You cancelled the sign-in process. Please try again when ready.',
    'no_code': 'Authentication failed. Please try signing in again.',
    'token_exchange_failed': 'Could not complete sign-in with Google. Please try again.',
    'userinfo_failed': 'Could not retrieve your Google account information. Please try again.',
    'db_error': 'A server error occurred during sign-in. Please try again.',
  };

  return errorMessages[errorCode || ''] || 'An unexpected error occurred during sign-in. Please try again.';
}

// Cache for Google Client ID to prevent re-fetching across mounts
let cachedGoogleClientId: string | null = null;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [trialAvailable, setTrialAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);
  const [isGoogleScriptReady, setIsGoogleScriptReady] = useState(false);
  const [welcomeSaleEligible, setWelcomeSaleEligible] = useState(false);

  const isAuthenticated = !!user;
  const hasActiveSubscription = !!(subscription && subscription.status === 'active');

  /**
   * Clear auth error message
   */
  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);

  /**
   * Open Google OAuth in new tab (primary) with redirect fallbacks
   */
  const signIn = () => {
    const authUrl = `${API_BASE_URL}/api/auth/google`;
    
    // Log for debugging
    console.log('🔐 Initiating Google Sign In...');
    console.log('Auth URL:', authUrl);
    
    // Clear any previous errors
    setAuthError(null);
    
    // Method 1 (Primary): Open in new tab/window
    // This is the best UX - user can see both the auth page and app
    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    
    const authWindow = window.open(
      authUrl,
      'GoogleSignIn',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
    
    // Check if popup was successfully opened
    if (authWindow && !authWindow.closed) {
      console.log('✅ Opened Google Sign In in new tab/window');
      
      // Listen for messages from the popup window
      const handleMessage = (event: MessageEvent) => {
        // Security: Verify message is from our own origin
        if (event.origin !== window.location.origin) {
          return;
        }
        
        if (event.data.type === 'AUTH_SUCCESS') {
          console.log('✅ Received auth success from popup');
          window.removeEventListener('message', handleMessage);
          // Refresh session to load user data
          refreshSession();
        } else if (event.data.type === 'AUTH_ERROR') {
          console.error('❌ Received auth error from popup:', event.data.error);
          window.removeEventListener('message', handleMessage);
          setAuthError(event.data.error || 'Authentication failed');
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      // Poll for window close as backup (in case postMessage fails)
      const pollTimer = setInterval(() => {
        try {
          if (authWindow.closed) {
            clearInterval(pollTimer);
            window.removeEventListener('message', handleMessage);
            console.log('🔄 Auth window closed, refreshing session...');
            // Refresh session to see if auth succeeded
            refreshSession();
          }
        } catch {
          // Window might be inaccessible due to cross-origin
          clearInterval(pollTimer);
          window.removeEventListener('message', handleMessage);
        }
      }, 500);
      
      // Cleanup timeout (10 minutes)
      setTimeout(() => {
        clearInterval(pollTimer);
        window.removeEventListener('message', handleMessage);
      }, 600000);
      
      return; // Success - exit function, don't execute fallbacks
    }
    
    // Popup was blocked - try fallback methods
    console.warn('⚠️ Popup was blocked, trying fallback redirect methods...');
    
    // Method 2 (Fallback): Try window.location.href redirect
    // This works in all browsers but navigates away from app
    try {
      console.log('🔄 Attempting redirect with window.location.href...');
      window.location.href = authUrl;
      return;
    } catch (redirectError) {
      console.warn('window.location.href failed:', redirectError);
      
      // Method 3 (Last Resort): Try window.location.assign
      try {
        console.log('🔄 Attempting redirect with window.location.assign...');
        window.location.assign(authUrl);
        return;
      } catch (assignError) {
        console.error('❌ All sign-in methods failed:', assignError);
        setAuthError('Unable to open sign-in page. Please check your browser settings and allow popups for this site.');
      }
    }
  };

  /**
   * Sign out - clear cookie + state
   */
  const signOut = useCallback(async () => {
    try {
      await unregisterFCMToken().catch(err => console.error('Failed to unregister FCM token:', err));
      await apiPost('/api/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      try {
        if (window.google?.accounts?.id) {
          window.google.accounts.id.cancel();
          window.google.accounts.id.disableAutoSelect?.();
        }
        localStorage.setItem(ONE_TAP_LOGOUT_KEY, Date.now().toString());
      } catch (err) {
        console.warn('Failed to persist One Tap logout cooldown:', err);
      }

      // Cleanup local storage for other services if shared
      localStorage.removeItem('nc_shipping');
      localStorage.removeItem('nc_notebooks');
      
      setUser(null);
      setSubscription(null);
      setTrialAvailable(true);
      setAuthError(null);
    }
  }, []);

  /**
   * Fetch current session from backend
   */
  const refreshSession = useCallback(async (): Promise<AuthUser | null> => {
    try {
      // Use cache-busting timestamp for session checks to avoid stale responses
      const data = await apiGet<SessionResponse>(`/api/auth/session?t=${Date.now()}`);
      if (data.success && data.data) {
        setUser(data.data.user);
        setSubscription(data.data.subscription);
        setWelcomeSaleEligible(data.data.welcome_sale_eligible ?? false);
        setAuthError(null);
        console.log('✅ Session loaded:', data.data.user.email);
        
        // Register token for push notifications when authenticated
        registerFCMToken().catch(err => console.error('Failed to register FCM token:', err));
        
        return data.data.user;
      }
    } catch {
      // Not authenticated or token expired
      setUser(null);
      setSubscription(null);
      console.log('ℹ️ No active session');
    }
    return null;
  }, []);

  /**
   * Fetch subscription status
   */
  const refreshSubscription = useCallback(async () => {
    if (!user) return;
    try {
      const data = await apiGet<SubscriptionStatusResponse>('/api/subscription/status');
      if (data.success && data.data) {
        setSubscription(data.data.subscription);
        setTrialAvailable(data.data.trial_available);
      }
    } catch (err) {
      console.error('Failed to refresh subscription:', err);
    }
  }, [user]);

  const initializeOneTap = useCallback(() => {
    if (!googleClientId || !isGoogleScriptReady || !window.google?.accounts?.id || isAuthenticated) return;

    try {
      const lastLogoutAt = Number(localStorage.getItem(ONE_TAP_LOGOUT_KEY) || '0');
      if (lastLogoutAt > 0 && Date.now() - lastLogoutAt < ONE_TAP_COOLDOWN_MS) {
        return;
      }
      if (lastLogoutAt > 0) {
        localStorage.removeItem(ONE_TAP_LOGOUT_KEY);
      }
    } catch {
      // Ignore storage errors; One Tap can continue.
    }

    try {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async ({ credential }: { credential: string }) => {
          if (!credential) return;

          try {
            const res = await apiPost<{ success: boolean; error?: string }>('/api/auth/one-tap', {
              credential,
            });

            if (!res.success) {
              setAuthError(res.error || 'Google One Tap sign-in failed.');
              return;
            }

            await refreshSession();
            await refreshSubscription();
          } catch (err) {
            console.error('One Tap sign-in error:', err);
          }
        },
        auto_select: true, // Auto sign-in enabled for better flow
        cancel_on_tap_outside: true,
        use_fedcm_for_prompt: false,
      });

      window.google.accounts.id.prompt();
    } catch (err) {
      console.warn('Failed to initialize Google One Tap:', err);
    }
  }, [googleClientId, isGoogleScriptReady, isAuthenticated, refreshSession, refreshSubscription]);

  useEffect(() => {
    const loadAuthConfigAndScript = async () => {
      try {
        if (cachedGoogleClientId) {
          setGoogleClientId(cachedGoogleClientId);
        } else {
          const cfg = await apiGet<AuthConfigResponse>('/api/auth/config');
          if (cfg.success && cfg.data?.google_client_id) {
            cachedGoogleClientId = cfg.data.google_client_id;
            setGoogleClientId(cfg.data.google_client_id);
          }
        }
      } catch (err) {
        console.warn('Failed to load auth config:', err);
      }

      if (window.google?.accounts?.id) {
        setIsGoogleScriptReady(true);
        return;
      }

      const existingScript = document.getElementById('google-identity-services') as HTMLScriptElement | null;
      if (existingScript) {
        existingScript.addEventListener('load', () => setIsGoogleScriptReady(true), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-identity-services';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => setIsGoogleScriptReady(true);
      script.onerror = () => console.warn('Failed to load Google Identity Services script');
      document.head.appendChild(script);
    };

    loadAuthConfigAndScript();
  }, []);

  /**
   * On mount: check for auth callback params, then load session
   */
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      // Check for auth callback
      const params = new URLSearchParams(window.location.search);
      const authResult = params.get('auth');

      // Check if we're in a popup window (opened by window.open)
      const isPopup = window.opener && window.opener !== window;

      if (authResult === 'success') {
        console.log('✅ Authentication successful!');
        
        if (isPopup) {
          // We're in the OAuth popup window - notify parent and close
          console.log('🔄 In popup window, notifying parent and closing...');
          
          try {
            // Try to notify parent window
            if (window.opener && !window.opener.closed) {
              window.opener.postMessage(
                { type: 'AUTH_SUCCESS' },
                window.location.origin
              );
            }
          } catch (e) {
            console.warn('Could not notify parent window:', e);
          }
          
          // Close popup after a brief delay
          setTimeout(() => {
            window.close();
          }, 500);
          
          return; // Don't continue initialization in popup
        }
        
        // Normal window - clean up URL
        const url = new URL(window.location.href);
        url.searchParams.delete('auth');
        window.history.replaceState({}, '', url.pathname + url.hash);
        
      } else if (authResult === 'error') {
        const message = params.get('message');
        const errorMessage = getHumanReadableError(message);
        console.error('❌ Auth error:', message);
        
        if (isPopup) {
          // In popup - notify parent about error and close
          console.log('❌ Auth error in popup, notifying parent...');
          
          try {
            if (window.opener && !window.opener.closed) {
              window.opener.postMessage(
                { type: 'AUTH_ERROR', error: errorMessage },
                window.location.origin
              );
            }
          } catch (e) {
            console.warn('Could not notify parent window:', e);
          }
          
          setTimeout(() => {
            window.close();
          }, 500);
          
          return; // Don't continue initialization in popup
        }
        
        // Normal window - show error
        setAuthError(errorMessage);
        
        // Clean up URL
        const url = new URL(window.location.href);
        url.searchParams.delete('auth');
        url.searchParams.delete('message');
        window.history.replaceState({}, '', url.pathname + url.hash);
      }

      // Always try to load session (cookie-based)
      await refreshSession();
      setIsLoading(false);
    };

    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      initializeOneTap();
    } else if (isAuthenticated && window.google?.accounts?.id) {
      window.google.accounts.id.cancel();
    }
  }, [isLoading, isAuthenticated, initializeOneTap]);

  const contextValue: AuthContextType = {
    user,
    subscription,
    trialAvailable,
    isLoading,
    isAuthenticated,
    hasActiveSubscription,
    authError,
    welcomeSaleEligible,
    signIn,
    signOut,
    refreshSession,
    refreshSubscription,
    clearAuthError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to use auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
